import { useState } from "react";
import { useParams } from "react-router";

import { Document, ActionEvent, ActionTargetType, ActionType } from "../../../../../types/working-model";
import { bytesToString, extractFileType } from "../../../../../utils/utils";
import Icon, { COLORS, ICONS } from "../../../../general-ui/icon";
import { TypographyItem } from "../../../../general-ui/typography-item/typography-item";
import { ParamsProps } from "../../../live-event";
import { useTracking } from '../../../../../utils/tracking';
import WaitingIndicator from "../../../../general-ui/waiting-indicator/waiting-indicator";
import { showAlert } from "../../../../general-ui/alert/alert-service";
import '../../../../../scss/live-event/base/session/document-card.scss';

interface DocProps {
	document: Document;
	template: string;
}

const downloadDoc = async (link: string, name: string): Promise<any> => {
	return fetch(link)
		.then(res => {
			return res.blob();
		})
		.then(blob => {
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.style.display = 'none';
			a.href = url;
			a.download = name;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
		})
		.catch(() => {
			showAlert({ message: "Download failed", description: `Cannot download file: ${name}`, duration: 3000, type: "error" });
		});
};

const DocumentCard: React.FC<DocProps> = ({ document, template }) => {
	const { language }: ParamsProps = useParams();

	const [downloading, setDownloading] = useState<boolean>(false);
	const file = extractFileType(document.original_name);
	const title = file.toUpperCase();
	const fileSize = bytesToString(document.filesize, 0);

	const { trackEvent } = useTracking();

	const handleDownload = async () => {
		setDownloading(true);
		trackEvent({
			action: ActionEvent.Download,
			action_type: ActionType.Active,
			target_type: ActionTargetType.Document,
			target_id: document.document
		});
		await downloadDoc(document.location, (document.display_name[language] || document.display_name.base) as string);
		setDownloading(false);
	};

	return (
		<div className="document">
			<div className="image-container">
				<div className="document-buttons">
					<a className="document-icon"
						rel="noreferrer"
						target="_blank"
						href={document.location}
						onClick={() => trackEvent({
							action: ActionEvent.View,
							action_type: ActionType.Active,
							target_type: ActionTargetType.Document,
							target_id: document.document
						})}
					>
						<Icon name={ICONS.VIEWERS_EYE} size={20} color={COLORS.WHITE} />
						<span>{title}</span>
					</a>
					<div className="document-icon" onClick={downloading ? () => { /**/ } : handleDownload}>
						{downloading ? (<WaitingIndicator />) : (<>
							<Icon name={ICONS.CLOUD_DOWNLOAD} size={18} color={COLORS.WHITE} />
							<span>{fileSize}</span>
						</>)}
					</div>
				</div>
				{document.thumbnail ? (
					<img
						src={document.thumbnail}
						alt={document.display_name.base}
					/>
				) : (
					<div className="document-placeholder"></div>
				)}
			</div>

			<div className="document-data">
				<TypographyItem tagName="h4" className="evt-heading-4" template={template}>
					{document.display_name[language] || document.display_name.base}
				</TypographyItem>
			</div>
		</div>
	);
};

export default DocumentCard;